<template>
  <div v-if="showModal" :class="$style.modalOverlay" @click="closeModal">
    <div :class="$style.modalContentLarge" @click.stop>
      <div :class="$style.modalHeader">
        <h3 :class="$style.modalTitle">{{ $t("teacher-applications-title") }}</h3>
        <div :class="$style.headerActions">
          <button type="button" :class="$style.templateButton" @click="openTemplateModal('shared')">
            Shared Template
          </button>
          <button type="button" :class="$style.templateButton" @click="openTemplateModal('mine')">
            My Template
          </button>
        </div>
        <button :class="$style.closeButton" @click="closeModal">
          <img :src="closeIcon" :alt="$t('close')" :class="$style.closeIcon" />
        </button>
      </div>
      <div :class="$style.modalBody">
        <div v-if="loading" :class="$style.loading">{{ $t("teacher-applications-loading") }}</div>
        <div v-else-if="error" :class="$style.error">{{ error }}</div>
        <div v-else-if="applications.length === 0" :class="$style.empty">{{ $t("teacher-applications-empty") }}</div>
        <div v-else :class="$style.applicationsList">
          <div
            v-for="app in applications"
            :key="app.id"
            :class="$style.applicationRow"
          >
            <div :class="$style.applicationInfo">
              <span :class="$style.applicationName">
                ({{ app.lastName }}, {{ app.firstName }})
              </span>
              <span :class="[$style.statusBadge, getStatusClass(app.status)]">
                {{ app.status }}
              </span>
            </div>
            <UtilButton
              :text="$t('view')"
              :clickFunction="() => viewApplication(app.id)"
              size="sm"
              backBg="#3f72af"
              frontBg="#5a8fc7"
              textColor="#FFFFFF"
              buttonWidth="80px"
            />
          </div>
        </div>
      </div>
      <div :class="$style.modalFooter">
        <button :class="$style.closeBtn" @click="closeModal">{{ $t("close") }}</button>
      </div>
    </div>

    <div v-if="templateModalType" :class="$style.templateModalOverlay" @click.stop="closeTemplateModal">
      <div :class="$style.templateModalContent" @click.stop>
        <div :class="$style.templateModalHeader">
          <h4 :class="$style.templatePanelTitle">{{ templateModalTitle }}</h4>
          <button type="button" :class="$style.panelCloseButton" @click="closeTemplateModal">
            {{ $t("close") }}
          </button>
        </div>

        <div v-if="templatesLoading" :class="$style.templateEmpty">Loading templates...</div>
        <div v-else :class="$style.templateManager">
          <aside :class="$style.templateListPanel">
            <button
              v-if="templateModalType === 'mine'"
              type="button"
              :class="$style.createTemplateButton"
              @click="startCreateTemplate"
            >
              Create your template
            </button>

            <div v-if="visibleTemplates.length === 0" :class="$style.templateEmpty">
              {{ templateModalType === "shared" ? "No shared templates yet." : "No templates created yet." }}
            </div>
            <div v-else :class="$style.templateRows">
              <button
                v-for="template in visibleTemplates"
                :key="template.id"
                type="button"
                :class="[
                  $style.templateRowButton,
                  selectedTemplate && selectedTemplate.id === template.id && $style.templateRowButtonActive,
                ]"
                @click="selectTemplate(template)"
              >
                <span :class="$style.templateRowText">
                  <span :class="$style.templateName">{{ template.name }}</span>
                  <span :class="$style.templateMeta">{{ visibilityLabel(template) }}</span>
                </span>
                <span :class="$style.viewDetailText">View Detail</span>
              </button>
            </div>
          </aside>

          <section :class="$style.templateDetailPanel">
            <div v-if="!selectedTemplate && !isTemplateFormOpen" :class="$style.templateEmpty">
              {{ templateModalType === "mine" ? "Select a template or create one." : "Select a shared template." }}
            </div>

            <template v-else-if="isTemplateFormOpen">
              <h5 :class="$style.templateDetailTitle">{{ templateDetailTitle }}</h5>
              <label :class="$style.templateFieldLabel">
                Template name
                <input
                  v-model="templateFormName"
                  :class="$style.templateNameInput"
                  type="text"
                  maxlength="256"
                  placeholder="Example: Accepted teacher message"
                />
              </label>
              <label :class="$style.templateFieldLabel">
                Template message
                <textarea
                  v-model="templateFormMessage"
                  :class="$style.templateTextarea"
                  placeholder="Write your reusable review template..."
                  rows="7"
                ></textarea>
              </label>
              <div :class="$style.visibilityOptions" role="radiogroup" aria-label="Template visibility">
                <label
                  :class="[
                    $style.visibilityOption,
                    templateFormVisibility === 'private' && $style.visibilityOptionActive,
                  ]"
                >
                  <input v-model="templateFormVisibility" type="radio" value="private" />
                  Private
                </label>
                <label
                  :class="[
                    $style.visibilityOption,
                    templateFormVisibility === 'shared' && $style.visibilityOptionActive,
                  ]"
                >
                  <input v-model="templateFormVisibility" type="radio" value="shared" />
                  Shared with all admins
                </label>
              </div>
              <div :class="$style.templateFormActions">
                <button
                  type="button"
                  :class="$style.saveTemplateButton"
                  :disabled="savingTemplate || !templateFormName.trim() || !templateFormMessage.trim()"
                  @click="saveTemplate"
                >
                  {{ editingTemplate ? "Save changes" : "Save template" }}
                </button>
                <button type="button" :class="$style.cancelTemplateButton" @click="cancelTemplateForm">
                  Cancel
                </button>
              </div>
            </template>

            <template v-else>
              <h5 :class="$style.templateDetailTitle">{{ selectedTemplate.name }}</h5>
              <div :class="$style.templateDetailMeta">{{ visibilityLabel(selectedTemplate) }}</div>
              <div :class="$style.templateDetailMessage">{{ selectedTemplate.message }}</div>
              <div v-if="templateModalType === 'mine'" :class="$style.templateDetailActions">
                <button type="button" :class="$style.saveTemplateButton" @click="startEditTemplate">
                  Change
                </button>
                <button
                  type="button"
                  :class="$style.deleteTemplateButton"
                  :disabled="deletingTemplate"
                  @click="deleteTemplate"
                >
                  Delete
                </button>
              </div>
            </template>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import styles from "@/css/TeacherApplicationsModal.module.css";
import CloseIcon from "@/assets/icons/close-red.svg";
import UtilButton from "@/components/util/UtilButton.vue";
import gameService from "@/services/game.service";
import { toast } from "vue3-toastify";

export default {
  name: "TeacherApplicationsModal",
  components: { UtilButton },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
    onViewApplication: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      applications: [],
      loading: false,
      error: null,
      templateModalType: null,
      templatesLoading: false,
      savingTemplate: false,
      deletingTemplate: false,
      creatingTemplate: false,
      editingTemplate: false,
      selectedTemplate: null,
      templateFormName: "",
      templateFormMessage: "",
      templateFormVisibility: "private",
      myTemplates: [],
      sharedTemplates: [],
      closeIcon: CloseIcon,
    };
  },
  computed: {
    $style() {
      return styles;
    },
    visibleTemplates() {
      return this.templateModalType === "shared" ? this.sharedTemplates : this.myTemplates;
    },
    templateModalTitle() {
      return this.templateModalType === "shared" ? "Shared Template" : "My Template";
    },
    templateDetailTitle() {
      if (this.creatingTemplate) return "Create your template";
      if (this.editingTemplate) return "Change template";
      return "Template detail";
    },
    isTemplateFormOpen() {
      return this.creatingTemplate || this.editingTemplate;
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        this.fetchApplications();
      } else {
        this.applications = [];
        this.error = null;
        this.closeTemplateModal();
      }
    },
  },
  methods: {
    openTemplateModal(type) {
      this.templateModalType = type;
      this.creatingTemplate = false;
      this.editingTemplate = false;
      this.selectedTemplate = null;
      this.resetTemplateForm();
      this.fetchTemplates();
    },
    closeTemplateModal() {
      this.templateModalType = null;
      this.templatesLoading = false;
      this.creatingTemplate = false;
      this.editingTemplate = false;
      this.selectedTemplate = null;
      this.resetTemplateForm();
    },
    normalizeTemplate(template) {
      const message = template?.message || "";
      const fallbackName = message.replace(/\s+/g, " ").trim();
      return {
        ...template,
        name: template?.name || (fallbackName ? fallbackName.slice(0, 48) : "Untitled template"),
        message,
      };
    },
    visibilityLabel(template) {
      return template?.isShared ? "Shared with all admins" : "Private";
    },
    async fetchTemplates() {
      this.templatesLoading = true;
      try {
        const res = await gameService.getReviewTemplates();
        if (res && res.success) {
          this.myTemplates = (res.myTemplates || []).map(this.normalizeTemplate);
          this.sharedTemplates = (res.sharedTemplates || []).map(this.normalizeTemplate);
        } else {
          toast.error(res?.message || "Failed to fetch templates.");
        }
      } catch (err) {
        toast.error("Failed to fetch templates.");
      } finally {
        this.templatesLoading = false;
      }
    },
    startCreateTemplate() {
      this.creatingTemplate = true;
      this.editingTemplate = false;
      this.selectedTemplate = null;
      this.resetTemplateForm();
    },
    selectTemplate(template) {
      this.selectedTemplate = this.normalizeTemplate(template);
      this.creatingTemplate = false;
      this.editingTemplate = false;
      this.resetTemplateForm();
    },
    startEditTemplate() {
      if (!this.selectedTemplate) return;
      this.creatingTemplate = false;
      this.editingTemplate = true;
      this.templateFormName = this.selectedTemplate.name || "";
      this.templateFormMessage = this.selectedTemplate.message || "";
      this.templateFormVisibility = this.selectedTemplate.isShared ? "shared" : "private";
    },
    cancelTemplateForm() {
      this.creatingTemplate = false;
      this.editingTemplate = false;
      this.resetTemplateForm();
    },
    resetTemplateForm() {
      this.templateFormName = "";
      this.templateFormMessage = "";
      this.templateFormVisibility = "private";
    },
    upsertTemplate(template) {
      const normalized = this.normalizeTemplate(template);
      this.myTemplates = [normalized, ...this.myTemplates.filter((item) => item.id !== normalized.id)];
      if (normalized.isShared) {
        this.sharedTemplates = [normalized, ...this.sharedTemplates.filter((item) => item.id !== normalized.id)];
      } else {
        this.sharedTemplates = this.sharedTemplates.filter((item) => item.id !== normalized.id);
      }
      this.selectedTemplate = normalized;
    },
    removeTemplate(templateId) {
      this.myTemplates = this.myTemplates.filter((item) => item.id !== templateId);
      this.sharedTemplates = this.sharedTemplates.filter((item) => item.id !== templateId);
      if (this.selectedTemplate?.id === templateId) this.selectedTemplate = null;
    },
    async saveTemplate() {
      if (this.savingTemplate || this.templateModalType !== "mine") return;

      const name = this.templateFormName.trim();
      const message = this.templateFormMessage.trim();
      if (!name || !message) return;

      const payload = {
        name,
        message,
        isShared: this.templateFormVisibility === "shared",
      };

      this.savingTemplate = true;
      try {
        const wasEditing = this.editingTemplate;
        const template = this.editingTemplate && this.selectedTemplate
          ? await gameService.updateReviewTemplate(this.selectedTemplate.id, payload)
          : await gameService.createReviewTemplate(payload);

        if (template && template.id) {
          this.upsertTemplate(template);
          this.creatingTemplate = false;
          this.editingTemplate = false;
          this.resetTemplateForm();
          toast.success(wasEditing ? "Template updated." : "Template saved.");
        } else {
          toast.error(template?.message || "Failed to save template.");
        }
      } catch (err) {
        toast.error("Failed to save template.");
      } finally {
        this.savingTemplate = false;
      }
    },
    async deleteTemplate() {
      if (!this.selectedTemplate || this.deletingTemplate || this.templateModalType !== "mine") return;
      const confirmed = window.confirm(`Delete template "${this.selectedTemplate.name}"?`);
      if (!confirmed) return;

      this.deletingTemplate = true;
      try {
        const res = await gameService.deleteReviewTemplate(this.selectedTemplate.id);
        if (res && res.success) {
          this.removeTemplate(this.selectedTemplate.id);
          toast.success("Template deleted.");
        } else {
          toast.error(res?.message || "Failed to delete template.");
        }
      } catch (err) {
        toast.error("Failed to delete template.");
      } finally {
        this.deletingTemplate = false;
      }
    },
    async fetchApplications() {
      this.loading = true;
      this.error = null;
      try {
        const res = await gameService.getAllTeacherApplications();
        if (res && res.success && res.applications) {
          this.applications = res.applications;
        } else {
          this.error = res?.message || this.$t("teacher-applications-fetch-failed");
          toast.error(this.error);
        }
      } catch (err) {
        this.error = this.$t("teacher-applications-fetch-error");
        toast.error(this.error);
      } finally {
        this.loading = false;
      }
    },
    viewApplication(applicationId) {
      this.onViewApplication(applicationId);
    },
    getStatusClass(status) {
      if (status === "accepted") return this.$style.statusAccepted;
      if (status === "declined") return this.$style.statusDeclined;
      return this.$style.statusPending;
    },
  },
};
</script>

<style module>
</style>
